
import Vue from 'vue'
import { mapActions, mapGetters, createNamespacedHelpers } from 'vuex'
import router from '@/router'

import { RButton, RLoader, RIcon, RCheckbox } from '@/components/'
import ProfileImage from '@/components/forms/ProfileImage.vue'

import { MemberWithTeamUuid } from '@/store/types/team'

const { mapActions: mapTeamActions, mapGetters: mapTeamGetters } = createNamespacedHelpers('team')

type Data = {
  toggledMembers: string[] | null
  name: string
  activeMember: number
  activeElement: string
}

export default Vue.extend({
  name: 'OwnedResource',
  components: {
    RButton,
    RLoader,
    RCheckbox,
    ProfileImage,
    RIcon,
  },
  props: {
    resourceType: {
      type: String,
    },
  },
  data(): Data {
    return {
      toggledMembers: null,
      name: '',
      activeMember: 0,
      activeElement: '',
    }
  },
  async mounted() {
    const { uuid, resourceType } = this
    await this.getResourceMembers({
      resourceType,
      uuid,
    })
    this.toggledMembers = this.membersInStore || []
    this.resourceType === 'workstation'
      ? await this.bindUserWorkstations()
      : await this.bindUserDrives()
    this.getResourceName()
    window.addEventListener('keyup', this.handleActive)
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleActive)
  },
  methods: {
    ...mapActions({
      bindUserDrives: 'drive/bindUserDrives',
      bindUserWorkstations: 'workstation/bindUserWorkstations',
    }),
    ...mapTeamActions({
      loadInvitesAndMembers: 'loadInvitesAndMembers',
      getResourceMembers: 'getCurrentResourceMembers',
      assignResource: 'assignResourceToMembers',
    }),
    async assign() {
      await this.assignResource({
        resourceType: this.resourceType,
        uuid: this.uuid,
        membersToAssignUuids: this.toggledMembers,
      })
      this.activeMember = 0
      this.activeElement = ''
    },
    toggled(memberUuid: string): boolean {
      if (!this.toggledMembers || !this.toggledMembers.length) {
        return false
      }
      return Boolean(this.toggledMembers.find((uuid) => uuid === memberUuid))
    },
    toggle(memberUuid: string): void {
      if (!this.toggledMembers) {
        return
      }
      if (this.toggled(memberUuid)) {
        this.toggledMembers = this.toggledMembers.filter((uuid) => uuid !== memberUuid)
      } else {
        this.toggledMembers.push(memberUuid)
      }
    },
    getResourceName(): string {
      if (this.resourceType === 'workstation') {
        return this.workstationInfo(this.uuid).name
          ? (this.name = this.workstationInfo(this.uuid).name)
          : 'Computer'
      } else {
        return this.driveInfo(this.uuid).name
          ? (this.name = this.driveInfo(this.uuid).name)
          : 'Drive'
      }
    },
    handleActive(e: KeyboardEvent): number | void {
      const allMembersUuids = this.members.map((member: MemberWithTeamUuid) => member.memberUuid)
      if (this.members.length) {
        if (e.key === 'ArrowDown') {
          if (this.activeMember < this.members.length - 1) {
            return (this.activeMember += 1)
          }
        }
        if (e.key === 'ArrowUp') {
          if (this.activeMember > 0) {
            return (this.activeMember -= 1)
          }
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (
            this.activeMember >= 0 &&
            this.members[this.activeMember] &&
            this.activeElement !== 'assign-button'
          ) {
            this.toggle(this.members[this.activeMember].memberUuid)
          }
        }
        if (e.key === 'Tab') {
          document.activeElement?.id ? (this.activeElement = document.activeElement.id) : ''
          this.activeMember = allMembersUuids.indexOf(this.activeElement)
        }
      }
    },
    handleClick(uuid: string, index: number): void {
      this.toggle(uuid)
      this.activeMember = index
    },
    isActive(uuid: string): boolean {
      return (
        this.members.length &&
        this.activeMember >= 0 &&
        uuid === this.members[this.activeMember].memberUuid &&
        this.activeElement !== 'assign-button'
      )
    },
  },
  computed: {
    ...mapGetters({
      driveInfo: 'drive/info',
      workstationInfo: 'workstation/info',
    }),
    ...mapTeamGetters({
      loadingTeam: 'loading',
      loadingCurrentResource: 'loadingCurrentResourceMembers',
      members: 'activeMembers',
      invites: 'invites',
      membersInStore: 'activeCurrentResourceMembers',
      pending: 'assignResourceRequestPending',
    }),
    loading(): boolean {
      return this.loadingTeam || this.loadingCurrentResource
    },
    uuid(): string {
      return router.currentRoute.params.pathMatch || ''
    },
    nothingSelected(): boolean {
      if (!this.toggledMembers) {
        return false
      }
      return this.toggledMembers.length === 0
    },
    hasMembers(): boolean {
      if (this.loading || !this.members) {
        return false
      }
      return this.members.length > 0
    },
  },
})
